import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Where each history entry was scrolled to, so going back returns there.
//
// "Volver" used to land at the footer. Traced on /agenda (list at 1500 px, open an event, Volver):
//   117 ms  the browser restored 1500 px, correctly, but onto the list page as it looks BEFORE its
//           data arrives: 2,771 px tall, and at 1500 px that short page shows the footer;
//   275 ms  the events arrived, the page grew by 4,670 px, and scroll anchoring kept the element
//           the reader was "looking at" (the footer) in view by moving the page 4,669 px down.
// So the reader ended at 6169 px, on the footer, every time. Layout also forced every route
// change to the top, which is right for a new page and wrong for going back.
//
// Now: the browser's restoration is off, positions are recorded per history entry as the reader
// scrolls, a new page opens at the top, and going back restores the recorded position once the
// page is tall enough to hold it, and keeps it there while the list loads, until the reader
// touches or scrolls. Until then it waits at the top: never at the footer.

const positions = new Map();   // location.key -> scrollY
const pathAt = new Map();      // history index -> pathname + search, for the app's back (see pathBehind)
const HOLD_MS = 2500;          // keep correcting while content loads in, at most this long
const GIVE_UP_MS = 5000;       // if the page never gets that tall, settle for the nearest point

if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

function restore(y) {
  let done = false;
  const t0 = performance.now();
  let reachedAt = null;
  const stop = () => {
    done = true;
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
    window.removeEventListener('pointerdown', stop);
  };
  // The reader taking over always wins: no position is worth fighting them for.
  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('keydown', stop);
  // pointerdown: a scrollbar drag fires no wheel or touch event, and was snapped back.
  window.addEventListener('pointerdown', stop, { passive: true });
  window.scrollTo(0, 0);
  // Back to the top of a page: nothing to wait for, and holding 0 would fight the page's own
  // scrollIntoView (a highlighted item, an anchor) for 2.5 s.
  if (y <= 0) { stop(); return stop; }
  const step = () => {
    if (done) return;
    const now = performance.now();
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (max >= y) {
      if (Math.abs(window.scrollY - y) > 1) window.scrollTo(0, y);
      if (reachedAt === null) reachedAt = now;
    } else if (now - t0 > GIVE_UP_MS) {
      window.scrollTo(0, Math.max(0, max));
      stop();
      return;
    }
    if (reachedAt !== null && now - reachedAt > HOLD_MS) { stop(); return; }
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
  return stop;
}

/** Layout calls this once. Replaces the old "scroll to top on every route change". */
export function useScrollMemory() {
  const location = useLocation();
  const navType = useNavigationType();
  // Updated during render, not in an effect. Opening a page first renders a SHORTER document, and
  // the browser clamps the scroll, firing a scroll event before any effect runs: with the key set
  // in an effect, that clamped value was saved against the page just left, and going back
  // returned to the wrong place.
  const keyRef = useRef(location.key);
  keyRef.current = location.key;

  useEffect(() => {
    // Recorded at the moment of the event, key and position together. A Map write per scroll
    // event costs nothing; deferring it to a frame let a later key pick up an earlier position.
    const onScroll = () => { positions.set(keyRef.current, window.scrollY); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const prevPath = useRef(location.pathname);
  useEffect(() => {
    const idx = window.history.state && window.history.state.idx;
    if (typeof idx === 'number') pathAt.set(idx, location.pathname + location.search);
    const samePage = prevPath.current === location.pathname;
    prevPath.current = location.pathname;
    if (navType === 'POP' && positions.has(location.key)) return restore(positions.get(location.key));
    // A new page opens at the top. Not a change within the same page: a filter that rewrites only
    // ?query also creates a new history entry, and must not throw the reader back to the top,
    // which is why the old effect was keyed on the pathname.
    if (!samePage) window.scrollTo(0, 0);
    return undefined;
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * True when `path` is the page right behind the current one in this tab's history.
 *
 * The app's native back button (and Android's) does not step back through web history: it sends
 * the previous path, which the page used to open as a NEW visit, at the top. When that path is
 * the entry right behind us, stepping back instead makes it a real back, so the position above is
 * restored.
 *
 * Only on an exact match, query string included. The app tracks pathnames only, and 300 ms after
 * sending one it checks that the page's pathname + search equals it, reloading the whole page
 * when it does not. Stepping back onto /torrebus?parada=5 when the app sent /torrebus therefore
 * ended in a full reload, which is worse than the plain new visit this replaced.
 */
export function pathBehind(path) {
  const idx = window.history.state && window.history.state.idx;
  if (typeof idx !== 'number' || idx < 1) return false;
  const behind = pathAt.get(idx - 1);
  return !!behind && behind === String(path);
}
